import React, { useRef } from "react";
import { useDrag } from "@use-gesture/react";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { Box } from "@UI/Components/Box/Box";
import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Button } from "@UI/Components/Button/Button";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Icon } from "@UI/Components/Icon/Icon";
import { ChevronLeft } from "@UI/Components/Icons/ChevronLeft";
import { ChevronRight } from "@UI/Components/Icons/ChevronRight";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import { CategoryCard } from "./CategoryCard";

const SliderButton = ({ onClick, icon }) => {
  const { isDesktop } = useMediaQuery();
  return (
    isDesktop && (
      <Button
        variant="ghost"
        w="32px"
        h="32px"
        _hover={{ bg: "sand.200" }}
        onClick={onClick}
      >
        <Icon
          as={icon}
          width="16px"
          height="16px"
          display={{ base: "none", md: "initial" }}
        />
      </Button>
    )
  );
};

export const ServiceCategorySlider = () => {
  const navigate = useNavigate();

  const sliderRef = useRef(null);

  const handleNext = () => {
    sliderRef.current.scrollLeft += sliderRef.current.offsetWidth / 2;
  };

  const handlePrev = () => {
    sliderRef.current.scrollLeft -= sliderRef.current.offsetWidth / 2;
  };

  const bind = useDrag(({ movement: [mx] }) => {
    if (sliderRef.current) {
      sliderRef.current.scrollLeft -= mx;
    }
  });

  const navigateToTaskCreation = (
    queryParams: {
      categoryId?: number;
      step?: string;
    } = {},
  ) => {
    const { categoryId, step } = queryParams;
    navigate("REQUEST_SERVICE", {
      reload: true,
      queryParams: {
        p_category: categoryId?.toString(),
        step,
      },
    });
  };

  return (
    <VStack w="100%" alignItems="start" spacing="20px" paddingBottom="32px">
      <Box wordBreak="break-word">
        <Heading
          as="h4"
          fontSize={{ base: "xl", md: "3xl" }}
          sx={{ "text-wrap": "wrap" }}
        >
          <FormattedMessage
            {...messages["serviceRequest.categoryCarousel.title"]}
          />
        </Heading>
        <Text fontSize="md">
          <FormattedMessage
            {...messages["serviceRequest.categoryCarousel.description"]}
          />
        </Text>
      </Box>
      <HStack
        alignSelf="center"
        justifyContent="center"
        width="100%"
        maxW={{ base: "640px", xl: "800px" }}
        spacing="12px"
      >
        <SliderButton icon={ChevronLeft} onClick={handlePrev} />

        <Box position="relative" flexGrow="1" overflow="hidden">
          <HStack
            ref={sliderRef}
            overflowX="auto"
            scrollBehavior="smooth"
            scrollSnapType="x mandatory"
            css={{
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
            spacing="12px"
            alignItems="stretch"
            // eslint-disable-next-line
            // @ts-ignore
            {...bind()}
          >
            {Array(10)
              .fill("")
              .map((_, index) => (
                <CategoryCard
                  key={index}
                  itemNumber={index + 1}
                  onClick={navigateToTaskCreation}
                />
              ))}
          </HStack>
        </Box>
        <SliderButton icon={ChevronRight} onClick={handleNext} />
      </HStack>

      <Button
        size="lg"
        colorScheme="seaGreen"
        alignSelf="center"
        onClick={() => navigateToTaskCreation()}
      >
        <FormattedMessage
          {...messages[
            "serviceRequest.categoryCarousel.createServiceRequestCTA"
          ]}
        />
      </Button>
    </VStack>
  );
};
